import { c as _c } from "react/compiler-runtime";
import { Box, styled, Tooltip, type SxProps, type Theme } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { NativeVaultColor } from '@/theme/colorSchemes';
import { BackupVaultCard, VaultCardAsAccount } from './backupVaultCard';
const Tag = styled('div')((props: {
  backgroundColor: string;
  additionalStyles?: object;
}) => ({
  cursor: 'default',
  borderRadius: '4px',
  fontSize: '12px',
  padding: '2px 8px',
  margin: '2px 4px',
  color: 'black',
  backgroundColor: props.backgroundColor,
  display: 'inline-block',
  ...props.additionalStyles
}));
function VaultTagFromId(t0) {
  const $ = _c(7);
  const {
    vaultId
  } = t0;
  const {
    vaults
  } = useBackupVaults();
  let t1;
  if ($[0] !== vaultId || $[1] !== vaults) {
    let t2;
    if ($[3] !== vaultId) {
      t2 = vault => vault.id === vaultId;
      $[3] = vaultId;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = vaults.find(t2);
    $[0] = vaultId;
    $[1] = vaults;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const vault_0 = t1;
  if (!vault_0) {
    return null;
  }
  let t2;
  if ($[5] !== vault_0) {
    t2 = <VaultTag vault={vault_0} />;
    $[5] = vault_0;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function VaultTag(t0) {
  const $ = _c(5);
  const {
    sx,
    vault
  } = t0;
  if (typeof vault === "string") {
    let t1;
    if ($[0] !== vault) {
      t1 = <VaultTagFromId vaultId={vault} />;
      $[0] = vault;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== sx || $[3] !== vault) {
    t1 = <BackupVaultTag sx={sx} vault={vault} />;
    $[2] = sx;
    $[3] = vault;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
export const AccountAsVaultTag = t0 => {
  const $ = _c(9);
  const {
    sx,
    cloudProvider,
    region
  } = t0;
  const accountLabel = cloudProvider === Provider.Azure ? "subscription" : cloudProvider === Provider.Gcp ? "project" : "account";
  const t1 = `Source ${accountLabel}`;
  let t2;
  if ($[0] !== cloudProvider || $[1] !== region || $[2] !== sx) {
    t2 = <VaultCardAsAccount sx={sx} cloudProvider={cloudProvider} region={region} />;
    $[0] = cloudProvider;
    $[1] = region;
    $[2] = sx;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== cloudProvider || $[5] !== sx || $[6] !== t1 || $[7] !== t2) {
    t3 = <VaultTagInner sx={sx} backgroundColor={NativeVaultColor} name={t1} cloudProvider={cloudProvider} vaultCard={t2} />;
    $[4] = cloudProvider;
    $[5] = sx;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const BackupVaultTag = t0 => {
  const $ = _c(8);
  const {
    sx,
    vault
  } = t0;
  const t1 = vault.backgroundColor || "black";
  let t2;
  if ($[0] !== vault) {
    t2 = <BackupVaultCard data={vault} editable={false} />;
    $[0] = vault;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== sx || $[3] !== t1 || $[4] !== t2 || $[5] !== vault.name || $[6] !== vault.vaultAttributes.cloudProvider) {
    t3 = <VaultTagInner sx={sx} backgroundColor={t1} name={vault.name} cloudProvider={vault.vaultAttributes.cloudProvider} vaultCard={t2} />;
    $[2] = sx;
    $[3] = t1;
    $[4] = t2;
    $[5] = vault.name;
    $[6] = vault.vaultAttributes.cloudProvider;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const VaultTagInner = t0 => {
  const $ = _c(16);
  const {
    sx,
    backgroundColor,
    name,
    cloudProvider,
    vaultCard
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "preventOverflow",
      options: {
        boundary: "viewport",
        altAxis: true,
        padding: 8
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      popper: {
        modifiers: [t1, {
          name: "flip",
          options: {
            fallbackPlacements: ["top", "bottom", "left", "right"]
          }
        }]
      },
      tooltip: {
        sx: {
          padding: 0,
          backgroundColor: "transparent",
          maxWidth: "300px"
        }
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== sx) {
    t3 = {
      ...sx,
      maxWidth: "140px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    };
    $[2] = sx;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = backgroundColor || "black";
  const t5 = CloudProviders[cloudProvider]?.displayName;
  let t6;
  if ($[4] !== t5) {
    t6 = <Box component="span" className="font-semibold mr-[4px]">{t5}</Box>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== name) {
    t7 = <Box component="span">{name}</Box>;
    $[6] = name;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t6 || $[11] !== t7) {
    t8 = <Tag data-testid="vault-tag" additionalStyles={t3} backgroundColor={t4}>{t6}{t7}</Tag>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8 || $[14] !== vaultCard) {
    t9 = <Tooltip disableInteractive={false} disableFocusListener={true} slotProps={t2} title={vaultCard}>{t8}</Tooltip>;
    $[13] = t8;
    $[14] = vaultCard;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
};